/**
 * Mobile Mega Menu - Script JavaScript complet
 * Version: 5.2 (Menu avec en-tête fixe, animations optimisées et intégration Shopify)
 * 
 * Un script JavaScript professionnel pour gérer un menu mobile accordéon
 * avec support multi-niveaux, animations fluides et optimisations de performance.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      container: '#MobileMegaMenu',
      overlay: '#MobileMegaMenuOverlay',
      hamburger: '.js-drawer-open-top, .mobile-nav-toggle, [aria-controls="header-sidebar-menu"]',
      closeBtn: '[data-mmm-close]',
      toggle: '[data-mmm-toggle]',
      toggleBtn: '[data-mmm-btn]',
      link: '[data-mmm-link]',
      submenu: '.mmm-submenu',
      level2Toggle: '[data-mmm-level2-btn]',
      level2Submenu: '.mmm-level2-submenu',
      cardWithSubmenu: '.mmm-card',
      featuredItems: '.mmm-featured-item',
      featuredWidgets: '.mmm-featured-widget',
      carousel: '.mmm-shipping-carousel'
    },
    classes: {
      active: 'active',
      noScroll: 'mmm-no-scroll',
      expanded: 'data-expanded'
    },
    attributes: {
      ariaHidden: 'aria-hidden',
      ariaExpanded: 'aria-expanded',
      dataBtnId: 'data-mmm-btn',
      dataToggleId: 'data-mmm-toggle',
      dataLevel2BtnId: 'data-mmm-level2-btn',
      dataExpanded: 'data-expanded',
      scrollPosition: 'data-scroll-position'
    },
    icons: {
      plus: '+',
      minus: '−'    // Vrai signe moins (U+2212)
    },
    timing: {
      animation: 250,    // ms - Correspond à --mmm-animation-speed dans CSS
      delay: 100,        // ms - Pour les opérations qui doivent attendre l'animation
      carousel: 6000     // ms - Intervalle entre les changements de slide (augmenté à 6 secondes)
    }
  };

  /**
   * Utilitaires
   */
  const Utils = {
    /**
     * Sélectionne un élément DOM
     * @param {string} selector - Sélecteur CSS
     * @param {Element} [context=document] - Contexte pour la recherche
     * @return {Element|null} - Élément trouvé ou null
     */
    select: function(selector, context = document) {
      return context.querySelector(selector);
    },

    /**
     * Sélectionne tous les éléments correspondant au sélecteur
     * @param {string} selector - Sélecteur CSS
     * @param {Element} [context=document] - Contexte pour la recherche
     * @return {Element[]} - Tableau d'éléments
     */
    selectAll: function(selector, context = document) {
      return Array.from(context.querySelectorAll(selector));
    },

    /**
     * Ajoute plusieurs écouteurs d'événement
     * @param {Element} element - Élément DOM
     * @param {string[]} events - Tableau d'événements à écouter
     * @param {Function} callback - Fonction à appeler
     */
    addMultipleEventListeners: function(element, events, callback) {
      events.forEach(event => element.addEventListener(event, callback));
    },

    /**
     * Debounce une fonction
     * @param {Function} func - Fonction à debouncer
     * @param {number} wait - Délai d'attente en ms
     * @return {Function} - Fonction debouncée
     */
    debounce: function(func, wait) {
      let timeout;
      return function() {
        const context = this;
        const args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    },

    /**
     * Détecte si l'appareil est tactile
     * @return {boolean} - True si l'appareil est tactile
     */
    isTouchDevice: function() {
      return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    },

    /**
     * Détecte si le navigateur est iOS
     * @return {boolean} - True si le navigateur est sur iOS
     */
    isIOS: function() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },

    /**
     * Active/désactive le défilement du document
     * @param {boolean} lock - Si true, verrouille le défilement
     */
    toggleBodyScroll: function(lock) {
      const body = document.body;
      const html = document.documentElement;
      
      if (lock) {
        // Stocker la position de défilement actuelle
        const scrollY = window.scrollY || window.pageYOffset;
        
        // Ajout des classes de blocage
        body.classList.add(CONFIG.classes.noScroll);
        html.classList.add(CONFIG.classes.noScroll);
        
        // Mémoriser la position de défilement
        body.setAttribute(CONFIG.attributes.scrollPosition, scrollY);
        
        // Fixer la position pour maintenir la vue actuelle
        body.style.top = `-${scrollY}px`;
        
        // Approche spécifique pour iOS
        if (this.isIOS()) {
          body.style.position = 'fixed';
          body.style.width = '100%';
        }
      } else {
        // Récupérer la position mémorisée
        const scrollY = parseInt(body.getAttribute(CONFIG.attributes.scrollPosition) || '0');
        
        // Supprimer les classes de blocage
        body.classList.remove(CONFIG.classes.noScroll);
        html.classList.remove(CONFIG.classes.noScroll);
        
        // Restaurer la position initiale
        body.style.top = '';
        body.style.position = '';
        body.style.width = '';
        
        // Rétablir la position de défilement
        window.scrollTo(0, scrollY);
      }
    }
  };

  /**
   * Gestionnaire de Menu Mobile
   */
  class MobileMenuManager {
    constructor() {
      // Éléments DOM
      this.container = Utils.select(CONFIG.selectors.container);
      if (!this.container || this.container.dataset.mmmInitialized) return;
      this.container.dataset.mmmInitialized = "true";

      this.overlay = Utils.select(CONFIG.selectors.overlay);
      this.closeBtn = Utils.select(CONFIG.selectors.closeBtn, this.container);
      this.hamburgerBtns = Utils.selectAll(CONFIG.selectors.hamburger);
      this.toggles = Utils.selectAll(CONFIG.selectors.toggle, this.container);
      this.toggleBtns = Utils.selectAll(CONFIG.selectors.toggleBtn, this.container);
      this.links = Utils.selectAll(CONFIG.selectors.link, this.container);
      this.submenus = Utils.selectAll(CONFIG.selectors.submenu, this.container);
      this.level2Toggles = Utils.selectAll(CONFIG.selectors.level2Toggle, this.container);
      this.cardsWithSubmenu = Utils.selectAll(CONFIG.selectors.cardWithSubmenu, this.container);
      this.featuredItems = Utils.selectAll(CONFIG.selectors.featuredItems, this.container);
      this.featuredWidgets = Utils.selectAll(CONFIG.selectors.featuredWidgets, this.container);
      this.carousel = Utils.select(CONFIG.selectors.carousel);

      // État
      this.isOpen = false;
      this.activeSubmenu = null;
      this.activeLevel2Submenus = new Set();
      this.scrollPosition = 0;
      this.carouselInterval = null;

      // Initialisation
      this.init();
    }

    /**
     * Initialise le menu
     */
    init() {
      // Initialiser les écouteurs d'événements
      this.setupEventListeners();
      
      // Override du drawer par défaut
      this.overrideDefaultDrawer();
      
      // Initialiser l'accessibilité
      this.setupAccessibility();
      
      // Initialiser les animations pour les éléments personnalisés
      this.initFeaturedItems();
      
      // Initialiser le carrousel de bannière
      this.initShippingCarousel();
      
      // Gestion des media queries pour fermer le menu sur grand écran
      this.setupMediaQueryListeners();
      
      console.log('Mobile Mega Menu initialisé');
    }
    
    /**
     * Configure les media queries pour fermer le menu sur grand écran
     */
    setupMediaQueryListeners() {
      const mediaQuery = window.matchMedia('(min-width: 1150px)');
      
      // Fermer le menu quand l'écran devient large
      const handleMediaChange = (e) => {
        if (e.matches && this.isOpen) {
          this.closeMenu();
        }
      };
      
      // Écouter les changements de taille d'écran
      mediaQuery.addEventListener('change', handleMediaChange);
    }

    /**
     * Initialise le carrousel de la bannière
     */
    initShippingCarousel() {
      const carousel = this.carousel;
      if (!carousel) return;
      
      const slides = Array.from(carousel.querySelectorAll('.mmm-carousel-slide'));
      const dots = Array.from(carousel.querySelectorAll('.mmm-carousel-dot'));
      
      if (!slides.length) return;
      
      let currentIndex = 0;
      const slideCount = slides.length;
      
      // Ne rien faire s'il n'y a qu'un seul slide
      if (slideCount <= 1) {
        const indicators = carousel.querySelector('.mmm-carousel-indicators');
        if (indicators) indicators.remove();
        return;
      }
      
      // Fonction pour passer au slide suivant avec fondu
      const goToSlide = (index) => {
        // Gérer les limites
        if (index < 0) index = slideCount - 1;
        if (index >= slideCount) index = 0;
        
        // Désactiver le slide actuel
        slides[currentIndex].classList.remove('active');
        if (dots[currentIndex]) { dots[currentIndex].classList.remove('active'); dots[currentIndex].setAttribute('aria-pressed', 'false'); }
        
        // Activer le nouveau slide
        slides[index].classList.add('active');
        if (dots[index]) { dots[index].classList.add('active'); dots[index].setAttribute('aria-pressed', 'true'); }
        
        currentIndex = index;
      };
      
      // Fonction pour passer automatiquement au slide suivant
      const autoAdvance = () => {
        goToSlide(currentIndex + 1);
      };
      
      // Gérer les clics sur les indicateurs
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          goToSlide(i);
          this.resetCarouselInterval();
        });
      });
      
      // Démarrer l'animation automatique
      this.carouselAutoAdvance = autoAdvance;
      this.startCarouselInterval();
    }
    
    /**
     * Démarre l'intervalle du carrousel
     */
    startCarouselInterval() {
      this.stopCarouselInterval();
      if (this.isOpen && this.carouselAutoAdvance && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.carouselInterval = setInterval(this.carouselAutoAdvance, CONFIG.timing.carousel);
      }
    }
    
    /**
     * Arrête l'intervalle du carrousel
     */
    stopCarouselInterval() {
      if (this.carouselInterval) {
        clearInterval(this.carouselInterval);
        this.carouselInterval = null;
      }
    }
    
    /**
     * Réinitialise l'intervalle du carrousel
     */
    resetCarouselInterval() {
      this.stopCarouselInterval();
      this.startCarouselInterval();
    }

    /**
     * Initialise les éléments de la section personnalisable et widgets
     */
    initFeaturedItems() {
      // Ajouter des effets de survol/clic sur les éléments personnalisés
      this.featuredItems.forEach(item => {
        item.addEventListener('touchstart', function() {
          this.style.transform = 'translateY(1px)';
          this.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.08)';
        }, { passive: true });
        
        item.addEventListener('touchend', function() {
          this.style.transform = '';
          this.style.boxShadow = '';
        }, { passive: true });
      });

      // Ajouter des effets similaires sur les widgets en vedette
      this.featuredWidgets.forEach(widget => {
        widget.addEventListener('touchstart', function() {
          const img = this.querySelector('.mmm-featured-widget-image-wrap');
          if (img) {
            img.style.transform = 'scale(0.98)';
          }
        }, { passive: true });
        
        widget.addEventListener('touchend', function() {
          const img = this.querySelector('.mmm-featured-widget-image-wrap');
          if (img) {
            img.style.transform = '';
          }
        }, { passive: true });
      });
    }

    /**
     * Configure les écouteurs d'événements
     */
    setupEventListeners() {
      // Ouverture du menu
      this.hamburgerBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openMenu();
        });
      });

      // Fermeture du menu
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeMenu();
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeMenu());
      }

      // Fermeture avec Échap
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeMenu();
        }
      });

      // Empêcher fermeture en cliquant dans le menu
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopCarouselInterval(); else if (this.isOpen) this.startCarouselInterval();
      });
      this.container.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Liens pour ne pas interférer avec les toggles
      this.links.forEach(link => {
        link.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      });

      // Gestion des accordéons niveau 1
      this.toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          // Ne pas déclencher pour les clics sur les liens
          if (!e.target.closest(CONFIG.selectors.link)) {
            const id = toggle.getAttribute(CONFIG.attributes.dataToggleId);
            if (id) {
              this.toggleSubmenu(id);
            }
          }
        });
      });

      this.toggleBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute(CONFIG.attributes.dataBtnId);
          if (id) {
            this.toggleSubmenu(id);
          }
        });
      });

      // Gestion des accordéons niveau 2
      this.level2Toggles.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute(CONFIG.attributes.dataLevel2BtnId);
          if (id) {
            this.toggleLevel2Submenu(id);
          }
        });
      });
      
      // Gestion de la fermeture lors du redimensionnement de la fenêtre
      window.addEventListener('resize', Utils.debounce(() => {
        if (window.innerWidth >= 1150 && this.isOpen) {
          this.closeMenu();
        }
      }, 150));
    }

    /**
     * Gère l'accessibilité
     */
    setupAccessibility() {
      // Ajouter les attributs d'accessibilité
      this.container.setAttribute('role', 'dialog');
      this.container.setAttribute('aria-modal', 'true');
      if (!this.container.hasAttribute('aria-label')) this.container.setAttribute('aria-label', 'Menu');
      this.container.inert = true;
      
      // Mettre tous les sous-menus en état fermé
      this.submenus.forEach(submenu => {
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
      });
      
      Utils.selectAll(CONFIG.selectors.level2Submenu, this.container).forEach(submenu => {
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
      });
      
      // Gestion du focus
      this.setupFocusTrap();
    }
    
    /**
     * Piège à focus pour l'accessibilité
     */
    setupFocusTrap() {
      this.container.addEventListener('keydown', (e) => {
        if (!this.isOpen || e.key !== 'Tab') return;
        
        const focusables = this.getFocusableElements();
        if (!focusables.length) return;
        
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        
        // Shift+Tab depuis le premier élément -> aller au dernier
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        // Tab depuis le dernier élément -> aller au premier
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
    
    /**
     * Récupère tous les éléments qui peuvent recevoir le focus
     * @return {Element[]} - Éléments focusables
     */
    getFocusableElements() {
      return Utils.selectAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        this.container
      ).filter(el => !el.disabled && el.getAttribute("tabindex") !== "-1" && !el.closest('[aria-hidden="true"], [inert]'));
    }

    /**
     * Ouvre/ferme un sous-menu de niveau 1
     * @param {string} id - ID du sous-menu
     */
    toggleSubmenu(id) {
      const submenu = Utils.select(`#mmm-submenu-${id}`, this.container);
      const icon = Utils.select(`#mmm-toggle-icon-${id}`, this.container);
      const btn = Utils.select(`[${CONFIG.attributes.dataBtnId}="${id}"]`, this.container);
      const card = Utils.select(`.mmm-card[data-mmm-card="${id}"]`, this.container);
      
      if (!submenu || !icon || !btn) return;
      
      const isHidden = submenu.getAttribute(CONFIG.attributes.ariaHidden) === 'true';
      
      // Fermer les autres sous-menus de niveau 1
      if (!isHidden) {
        // Si on ferme, réinitialiser l'état
        this.activeSubmenu = null;
        
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
        icon.textContent = CONFIG.icons.plus;
        btn.setAttribute(CONFIG.attributes.ariaExpanded, 'false');
        
        // Désactiver le fond gris
        if (card) {
          card.setAttribute(CONFIG.attributes.dataExpanded, 'false');
        }
        
        // Fermer tous les sous-menus de niveau 2 dans ce sous-menu
        this.resetLevel2Submenus(submenu);
      } else {
        // Fermer tous les autres sous-menus de niveau 1
        this.submenus.forEach(menu => {
          if (menu !== submenu) {
            const menuId = menu.id.replace('mmm-submenu-', '');
            const menuIcon = Utils.select(`#mmm-toggle-icon-${menuId}`, this.container);
            const menuBtn = Utils.select(`[${CONFIG.attributes.dataBtnId}="${menuId}"]`, this.container);
            const menuCard = Utils.select(`.mmm-card[data-mmm-card="${menuId}"]`, this.container);
            
            if (menuIcon) menuIcon.textContent = CONFIG.icons.plus;
            if (menuBtn) menuBtn.setAttribute(CONFIG.attributes.ariaExpanded, 'false');
            menu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
            
            // Désactiver le fond gris
            if (menuCard) {
              menuCard.setAttribute(CONFIG.attributes.dataExpanded, 'false');
            }
            
            // Fermer tous les sous-menus de niveau 2 dans ce sous-menu
            this.resetLevel2Submenus(menu);
          }
        });
        
        // Ouvrir le sous-menu actuel
        this.activeSubmenu = id;
        
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'false');
        icon.textContent = CONFIG.icons.minus;
        btn.setAttribute(CONFIG.attributes.ariaExpanded, 'true');
        
        // Activer le fond gris
        if (card) {
          card.setAttribute(CONFIG.attributes.dataExpanded, 'true');
        }
      }
    }

    /**
     * Ouvre/ferme un sous-menu de niveau 2
     * @param {string} id - ID du sous-menu de niveau 2
     */
    toggleLevel2Submenu(id) {
      const submenu = Utils.select(`#mmm-level2-${id}`, this.container);
      const icon = Utils.select(`#mmm-level2-icon-${id}`, this.container);
      const btn = Utils.select(`[${CONFIG.attributes.dataLevel2BtnId}="${id}"]`, this.container);
      
      if (!submenu || !icon || !btn) return;
      
      const isActive = submenu.classList.contains(CONFIG.classes.active);
      
      if (isActive) {
        // Fermer le sous-menu
        submenu.classList.remove(CONFIG.classes.active);
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
        icon.textContent = CONFIG.icons.plus;
        btn.setAttribute(CONFIG.attributes.ariaExpanded, 'false');
        
        // Mettre à jour l'état
        this.activeLevel2Submenus.delete(id);
      } else {
        // Ouvrir le sous-menu
        submenu.classList.add(CONFIG.classes.active);
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'false');
        icon.textContent = CONFIG.icons.minus;
        btn.setAttribute(CONFIG.attributes.ariaExpanded, 'true');
        
        // Mettre à jour l'état
        this.activeLevel2Submenus.add(id);
      }
    }

    /**
     * Réinitialise tous les sous-menus de niveau 2 dans un sous-menu
     * @param {Element} parentSubmenu - Sous-menu parent
     */
    resetLevel2Submenus(parentSubmenu) {
      if (!parentSubmenu) return;
      
      const level2Submenus = Utils.selectAll(CONFIG.selectors.level2Submenu, parentSubmenu);
      
      level2Submenus.forEach(submenu => {
        submenu.classList.remove(CONFIG.classes.active);
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
        
        const id = submenu.id.replace('mmm-level2-', '');
        const icon = Utils.select(`#mmm-level2-icon-${id}`, this.container);
        const btn = Utils.select(`[${CONFIG.attributes.dataLevel2BtnId}="${id}"]`, this.container);
        
        if (icon) icon.textContent = CONFIG.icons.plus;
        if (btn) btn.setAttribute(CONFIG.attributes.ariaExpanded, 'false');
        
        // Mettre à jour l'état
        this.activeLevel2Submenus.delete(id);
      });
    }

    /**
     * Ouvre le menu
     */
    openMenu() {
      if (this.isOpen) return;
      
      // Mémoriser la position de défilement avant l'ouverture
      this.scrollPosition = window.scrollY || window.pageYOffset;
      
      this.returnFocus = document.activeElement;
      this.container.inert = false;
      this.hamburgerBtns.forEach(btn => btn.setAttribute("aria-expanded", "true"));
      // Ouvrir le menu
      this.container.setAttribute(CONFIG.attributes.ariaHidden, 'false');
      
      if (this.overlay) {
        this.overlay.classList.add(CONFIG.classes.active);
      }
      
      // Empêcher le défilement du body
      Utils.toggleBodyScroll(true);
      
      // Mettre à jour l'état
      this.isOpen = true;
      
      // Démarrer le carrousel si présent
      this.startCarouselInterval();
      
      // Focusing the dialog keeps the phone keyboard closed until search is tapped.
      setTimeout(() => { if (this.isOpen) this.container.focus({preventScroll: true}); }, CONFIG.timing.delay);
    }

    /**
     * Ferme le menu
     */
    closeMenu() {
      if (!this.isOpen) return;
      
      if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({preventScroll: true});
      this.container.inert = true;
      this.hamburgerBtns.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
      this.container.setAttribute(CONFIG.attributes.ariaHidden, 'true');
      
      if (this.overlay) {
        this.overlay.classList.remove(CONFIG.classes.active);
      }
      
      // Réactiver le défilement du body
      Utils.toggleBodyScroll(false);
      
      // Réinitialiser tous les sous-menus
      this.resetAllMenus();
      
      // Arrêter le carrousel
      this.stopCarouselInterval();
      
      // Mettre à jour l'état
      this.isOpen = false;
      this.activeSubmenu = null;
      this.activeLevel2Submenus.clear();
    }

    /**
     * Réinitialise tous les sous-menus
     */
    resetAllMenus() {
      // Niveau 1
      this.submenus.forEach(submenu => {
        const id = submenu.id.replace('mmm-submenu-', '');
        const icon = Utils.select(`#mmm-toggle-icon-${id}`, this.container);
        const btn = Utils.select(`[${CONFIG.attributes.dataBtnId}="${id}"]`, this.container);
        const card = Utils.select(`.mmm-card[data-mmm-card="${id}"]`, this.container);
        
        submenu.setAttribute(CONFIG.attributes.ariaHidden, 'true');
        if (icon) icon.textContent = CONFIG.icons.plus;
        if (btn) btn.setAttribute(CONFIG.attributes.ariaExpanded, 'false');
        
        // Désactiver le fond gris
        if (card) {
          card.setAttribute(CONFIG.attributes.dataExpanded, 'false');
        }
        
        // Niveau 2 dans ce sous-menu
        this.resetLevel2Submenus(submenu);
      });
    }

    /**
     * Pas d'override — les clics sont gérés dans setupEventListeners()
     */
    overrideDefaultDrawer() {
      // Intentionnellement vide — on intercepte uniquement via les click listeners
      // sur les boutons hamburger dans setupEventListeners()
    }
  }

  /**
   * Initialise le menu quand le DOM est chargé
   */
  let manager;
  function initialize() { manager = new MobileMenuManager(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  window.MobileMegaMenu = {
    open: function() { if (manager) manager.openMenu(); },
    close: function() { if (manager) manager.closeMenu(); }
  };
})();
